type TimestampLike = {
    toMillis?: () => number
    seconds?: number
    nanoseconds?: number
    _seconds?: number
    _nanoseconds?: number
}

export interface SurveyRecordWithTimestamps {
    id?: string
    completedAt?: unknown
    updatedAt?: unknown
    createdAt?: unknown
}

function toMillis(value: unknown): number {
    if (!value) {
        return 0
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0
    }

    if (typeof value === 'string') {
        const parsed = Date.parse(value)
        return Number.isFinite(parsed) ? parsed : 0
    }

    if (value instanceof Date) {
        return value.getTime()
    }

    if (typeof value === 'object') {
        const timestamp = value as TimestampLike

        if (typeof timestamp.toMillis === 'function') {
            return timestamp.toMillis()
        }

        const seconds = timestamp.seconds ?? timestamp._seconds
        if (typeof seconds === 'number') {
            const nanoseconds = timestamp.nanoseconds ?? timestamp._nanoseconds ?? 0
            return (seconds * 1000) + Math.floor(nanoseconds / 1_000_000)
        }
    }

    return 0
}

function getSurveyRecencyValue(record: SurveyRecordWithTimestamps): number {
    return toMillis(record.completedAt) || toMillis(record.updatedAt) || toMillis(record.createdAt)
}

function compareSurveyRecency<T extends SurveyRecordWithTimestamps>(a: T, b: T): number {
    const recencyDelta = getSurveyRecencyValue(b) - getSurveyRecencyValue(a)
    if (recencyDelta !== 0) {
        return recencyDelta
    }

    return (b.id || '').localeCompare(a.id || '')
}

export function selectLatestSurveyRecord<T extends SurveyRecordWithTimestamps>(surveys: readonly T[]): T | null {
    if (surveys.length === 0) {
        return null
    }

    return [...surveys].sort(compareSurveyRecency)[0] ?? null
}

export function isSurveyRecordNewer<T extends SurveyRecordWithTimestamps>(candidate: T, current?: T): boolean {
    if (!current) {
        return true
    }

    return compareSurveyRecency(candidate, current) < 0
}
